import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  MessageCircle, Users, TrendingUp, Clock, Sparkles, 
  ChevronRight, Trash2, Eye, RefreshCw, BarChart3,
  MessageSquare, Calendar, Hash
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ title, value, subtitle, icon: Icon, color }) => (
  <Card className="relative overflow-hidden">
    <CardContent className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <p className={`text-3xl font-bold ${color}`}>{value}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
        </div>
        <div className={`w-12 h-12 rounded-xl ${color.replace('text-', 'bg-').replace('600', '100')} flex items-center justify-center`}>
          <Icon className={`w-6 h-6 ${color}`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const AdminChatAnalytics = () => {
  const { adminAuth } = useOutletContext();
  const [analytics, setAnalytics] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [totalSessions, setTotalSessions] = useState(0);
  const [selectedSession, setSelectedSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const headers = { Authorization: `Basic ${adminAuth}` };
      
      const [analyticsRes, sessionsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/chat-analytics`, { headers }),
        fetch(`${API_URL}/api/admin/chat-sessions?limit=20`, { headers })
      ]);
      
      if (analyticsRes.ok) {
        setAnalytics(await analyticsRes.json());
      }
      
      if (sessionsRes.ok) {
        const data = await sessionsRes.json();
        setSessions(data.sessions || []);
        setTotalSessions(data.total || 0);
      }
    } catch (err) {
      console.error('Failed to fetch chat analytics:', err);
      toast.error('Failed to load chat analytics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [adminAuth]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const viewSession = async (sessionId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/chat-sessions/${sessionId}`, {
        headers: { Authorization: `Basic ${adminAuth}` }
      });
      if (res.ok) {
        setSelectedSession(await res.json());
      }
    } catch (err) {
      toast.error('Failed to load conversation');
    }
  };

  const deleteSession = async (sessionId) => {
    if (!window.confirm('Delete this chat session?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/chat-sessions/${sessionId}`, {
        method: 'DELETE',
        headers: { Authorization: `Basic ${adminAuth}` }
      });
      if (res.ok) {
        toast.success('Session deleted');
        setSessions(sessions.filter(s => s.session_id !== sessionId));
        if (selectedSession?.session_id === sessionId) {
          setSelectedSession(null);
        }
      }
    } catch (err) {
      toast.error('Failed to delete session');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Sparkles className="w-8 h-8 text-amber-500" />
            NavMind Analytics
          </h1>
          <p className="text-gray-600 mt-1">Monitor AI chat assistant conversations and engagement</p>
        </div>
        <Button onClick={handleRefresh} disabled={refreshing} variant="outline">
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Grid */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Total Conversations"
            value={analytics.overview.total_sessions}
            subtitle={`${analytics.activity.sessions_24h} in last 24h`}
            icon={MessageCircle}
            color="text-blue-600"
          />
          <StatCard
            title="Total Messages"
            value={analytics.overview.total_messages}
            subtitle={`${analytics.activity.messages_24h} in last 24h`}
            icon={MessageSquare}
            color="text-green-600"
          />
          <StatCard
            title="Avg. Messages/Chat"
            value={analytics.overview.avg_messages_per_session}
            subtitle="Per conversation"
            icon={BarChart3}
            color="text-purple-600"
          />
          <StatCard
            title="Active (7 days)"
            value={analytics.activity.sessions_7d}
            subtitle={`${analytics.activity.sessions_30d} in last 30 days`}
            icon={TrendingUp}
            color="text-amber-600"
          />
        </div>
      )}

      {/* Popular Topics */}
      {analytics?.popular_topics?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Hash className="w-5 h-5 text-amber-500" />
              Popular Topics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {analytics.popular_topics.map((topic) => (
                <div
                  key={topic.topic}
                  className="px-4 py-2 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-full flex items-center gap-2"
                >
                  <span className="font-medium text-amber-800 capitalize">{topic.topic}</span>
                  <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">
                    {topic.count}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Two Column Layout: Sessions List + Conversation Detail */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sessions List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-500" />
                Recent Conversations
              </span>
              <span className="text-sm font-normal text-gray-500">
                {totalSessions} total
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y max-h-[500px] overflow-y-auto">
              {sessions.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No conversations yet</p>
                </div>
              ) : (
                sessions.map((session) => (
                  <div
                    key={session.session_id}
                    className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                      selectedSession?.session_id === session.session_id ? 'bg-amber-50 border-l-4 border-amber-500' : ''
                    }`}
                    onClick={() => viewSession(session.session_id)}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900 truncate font-medium">
                          {session.preview}
                        </p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <MessageSquare className="w-3 h-3" />
                            {session.message_count} messages
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(session.updated_at)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0"
                          onClick={(e) => { e.stopPropagation(); viewSession(session.session_id); }}
                        >
                          <Eye className="w-4 h-4 text-gray-400" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 hover:bg-red-50"
                          onClick={(e) => { e.stopPropagation(); deleteSession(session.session_id); }}
                        >
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Conversation Detail */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Eye className="w-5 h-5 text-green-500" />
              Conversation Detail
            </CardTitle>
          </CardHeader>
          <CardContent>
            {selectedSession ? (
              <div className="space-y-4">
                <div className="text-xs text-gray-500 flex items-center gap-4 pb-3 border-b">
                  <span>Session: {selectedSession.session_id.slice(0, 20)}...</span>
                  <span>Started: {formatDate(selectedSession.created_at)}</span>
                </div>
                <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                  {selectedSession.messages?.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[85%] p-3 rounded-2xl text-sm ${
                          msg.role === 'user'
                            ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-br-md'
                            : 'bg-gray-100 text-gray-800 rounded-bl-md'
                        }`}
                      >
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                        <p className={`text-xs mt-1 ${msg.role === 'user' ? 'text-white/70' : 'text-gray-400'}`}>
                          {formatDate(msg.timestamp)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <Eye className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Select a conversation to view details</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminChatAnalytics;
